import React from "react";
import { useNavigate } from "react-router-dom";
import { TargetXHeader } from "../../components/TargetXHeader";

interface AppCardSpec {
  key: string;
  title: string;
  slug: string;
  description: string;
  tagline?: string;
  comingSoon?: boolean;
  actionLabel?: string;
  route?: string;
  icon: React.ReactNode;
  iconBg?: string;
}

// Updated icons to match sidebar - 2025-09-15
const GeneIcon = (
  <span className="text-2xl" role="img" aria-label="Gene">
    🧬
  </span>
);
const DiseaseIcon = (
  <span className="text-2xl" role="img" aria-label="Disease">
    🩺
  </span>
);
const LinkIcon = (
  <span className="text-2xl" role="img" aria-label="Link">
    🔗
  </span>
);

const CARDS: AppCardSpec[] = [
  {
    key: "gene-hub",
    title: "Gene Hub",
    slug: "Gene Hub",
    description:
      "Generate target gene lists from your favorite data. Understand how genes are connected, and their functions.",
    tagline: "CURATE & EXPLORE GENE SETS",
    route: "/gene-hub",
    actionLabel: "Open",
    icon: GeneIcon,
    iconBg: "from-purple-500 to-indigo-600",
  },
  {
    key: "disease-hub",
    title: "Disease Hub",
    slug: "Disease Hub",
    description:
      "Prioritize diseases for targets for disease subpopulations. Predict or explain biomarker associations.",
    tagline: "Stratify disease signals",
    comingSoon: true, // Disease Hub is now ready!
    route: "/disease-hub",
    actionLabel: "Open",
    icon: DiseaseIcon,
    iconBg: "from-rose-500 to-orange-500",
  },
  {
    key: "gene-disease-link",
    title: "Gene Disease Link",
    slug: "Gene-Disease Link",
    description:
      "Is there a link between gene and disease? What could be the mechanism?",
    tagline: "Mechanistic relationship explorer",
    route: "/research/gene-disease-link",
    actionLabel: "Open",
    icon: LinkIcon,
    iconBg: "from-emerald-500 to-teal-500",
  },
];

const Card: React.FC<{ spec: AppCardSpec }> = ({ spec }) => {
  const navigate = useNavigate();
  const disabled = !!spec.comingSoon || !spec.route;

  const activate = () => {
    if (!disabled && spec.route) navigate(spec.route);
  };
  const onKey: React.KeyboardEventHandler<HTMLDivElement> = (e) => {
    if (disabled) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      activate();
    }
  };
  return (
    <div
      role={!disabled ? "button" : undefined}
      tabIndex={!disabled ? 0 : -1}
      aria-disabled={disabled || undefined}
      onClick={activate}
      onKeyDown={onKey}
      className={`group relative flex flex-col items-center text-center rounded-2xl border p-8 transition-all select-none outline-none
        ${
          disabled
            ? "bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 opacity-70 cursor-not-allowed"
            : "bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 shadow-sm hover:shadow-md focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-slate-900"
        }
        ${
          !disabled
            ? "hover:-translate-y-0.5 active:translate-y-px hover:[&_h3]:text-indigo-600 dark:hover:[&_h3]:text-indigo-400"
            : ""
        }
      `}
    >
      <div
        className={`mb-5 inline-flex items-center justify-center w-16 h-16 rounded-2xl text-white shadow-lg bg-gradient-to-br ${
          spec.iconBg || "from-slate-600 to-slate-800"
        } ring-4 ring-white/40 dark:ring-slate-900/60`}
        aria-hidden="true"
      >
        {spec.icon}
      </div>
      <h3
        className="text-xl font-semibold text-slate-800 dark:text-slate-100 transition-colors"
        id={`${spec.key}-title`}
      >
        {spec.slug}
      </h3>
      {spec.tagline && (
        <p className="mt-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
          {spec.tagline}
        </p>
      )}
      <p className="mt-4 text-sm leading-relaxed text-slate-600 dark:text-slate-400 max-w-xs">
        {spec.description}
      </p>
      <div className="sr-only" aria-live="polite">
        {disabled ? "Coming soon" : "Available"}
      </div>
      {!disabled && (
        <span className="mt-6 inline-block text-[11px] font-semibold tracking-wide text-indigo-600 dark:text-indigo-400 group-hover:underline">
          {spec.actionLabel || "Open"}
        </span>
      )}
      {disabled && (
        <span className="mt-6 inline-block text-[11px] font-semibold tracking-wide text-slate-400 dark:text-slate-500">
          Coming soon
        </span>
      )}
    </div>
  );
};

export const Dashboard: React.FC = () => {
  return (
    <div className="min-h-full w-full bg-gradient-to-b from-white via-slate-50 to-white dark:from-slate-950 dark:via-slate-900 dark:to-slate-950">
      <TargetXHeader
        title="TargetX"
        subtitle="A Target Discovery Platform that revolutionizes Drug Discovery with AI and Knowledge Graph"
      />
      {/* <header className="max-w-7xl mx-auto px-8 pt-12 pb-10 text-center">
        <div className="flex items-center justify-center space-x-4 mb-4">
          <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-xl">
            <span className="text-white font-bold text-2xl">T</span>
          </div>
          <h1 className="text-4xl font-extrabold tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-purple-600 to-blue-600">
            TargetX
          </h1>
        </div>
        <p className="mt-4 text-slate-600 dark:text-slate-300 max-w-3xl mx-auto text-lg">
          A Target Discovery Platform that revolutionizes Drug Discovery with AI
          and Knowledge Graph
        </p>
        <hr className="mt-10 border-slate-200 dark:border-slate-700/60" />
      </header> */}

      <main className="max-w-7xl mx-auto px-8 pb-24">
        <h2 className="text-2xl font-bold text-center my-10 text-slate-800 dark:text-slate-100">
          Explore Our Apps
        </h2>
        <div className="grid gap-10 md:grid-cols-2 lg:grid-cols-3">
          {CARDS.map((c) => (
            <Card key={c.key} spec={c} />
          ))}
        </div>
      </main>
    </div>
  );
};
